import React, { useEffect, useRef, useState } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import VendorList from 'features/Vendors/components/VendorList'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import notify from 'components/ui/notify'
import { Users, Handshake, UserPlus } from 'lucide-react'
import useTableNoPage from 'components/controls/useTableNoPage'
import Message from 'components/Message'
import Spinner from 'components/Spinner'
import Layout from 'components/Layout'
import Popup from 'components/Popup'
import { PartnerCard, CardGridCN } from 'components/Card'
import Button from 'components/Button'
import { Button as ShadButton } from 'components/ui/button'
import UserAddNew from 'components/UserAddNew'
import PartnersEmptyState from 'components/PartnersEmptyState'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'

import { myPartnersService } from 'services'

import { initializeInvitedUsers } from 'slices/invitedUserListSlice'
import { initializeUsers } from 'slices/usersSlice'
import { inviteNewUser } from 'slices/invitedUserSlice'
import {
	acceptMyPartner,
	addMyPartner,
	getMyPendingInvitations,
	getMyPendingPartnerShips,
	initializeMyPartners,
	withdrawMyPartnerInvitation,
} from 'slices/myPartnersSlice'
import MobileBackButton from 'components/MobileBackButton'
import usePartnerProfileDialog from 'features/Partners/hooks/usePartnerProfileDialog'

function formatInviteDate(dateStr) {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const Partners_Index = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const { openPartner, partnerDialog } = usePartnerProfileDialog()

	const searchInputRef = useRef(null)
	const [searchInputValue, setSearchInputValue] = useState('')
	const [openPopup, setOpenPopup] = useState(false)
	const [showConnectionsDialog, setShowConnectionsDialog] = useState(false)
	const [showInvitationsDialog, setShowInvitationsDialog] = useState(false)
	// Partnership id currently being accepted or rejected, so its two buttons
	// can be disabled without freezing the whole list.
	const [respondingToId, setRespondingToId] = useState(null)

	// User info
	const userLogin = useSelector(state => state.userLogin)
	const { userInfo } = userLogin
	const userid = userInfo._id

	// Pending invitations — people who invited ME
	const {
		entities: pendingEntities,
		ids: pendingIds,
		isInitialized,
	} = useSelector(state => state.myPartners.myPendingInvitations)
	const myPendingInvitationsList = pendingIds.map(id => pendingEntities[id])

	// Pending connections — people I invited
	const {
		entities: partnershipEntities,
		ids: partnershipIds,
		isInitialized: partnershipsInitialized,
	} = useSelector(state => state.myPartners.myPendingPartnerShips)
	const myPendingPartnerShipsList = partnershipIds.map(id => partnershipEntities[id])

	// Invited users
	const {
		entities: invitedUsers,
		ids: invitedUserIds,
		isInitialized: invitedUsersInitialized,
	} = useSelector(state => state.invitedUserList)

	// User list (for partner cards)
	const {
		entities: userList,
		ids: userListIds,
		isLoading: userListLoading,
		error,
	} = useSelector(state => state.userList)
	const users = userListIds.map(id => userList[id])

	// My partners
	const {
		entities: myPartners,
		ids: myPartnerIds,
		isLoading: myPartnersLoading,
		isInitialized: myPartnersInitialized,
	} = useSelector(state => state.myPartners)
	const myPartnersList = myPartnerIds.map(id => myPartners[id])

	const [filterFn, setFilterFn] = useState({ fn: items => items })

	const headerCells = [{ id: 'name', label: 'Partner' }]

	const fromUser = {
		id: userInfo._id,
		name: userInfo.name,
		email: userInfo.email,
		company: userInfo.company,
	}

	// Initialize data
	useEffect(() => {
		if (!isInitialized) {
			dispatch(getMyPendingInvitations())
		}
		if (!partnershipsInitialized) {
			dispatch(getMyPendingPartnerShips())
		}
		if (!invitedUsersInitialized) {
			dispatch(initializeInvitedUsers())
		}
		dispatch(initializeUsers())
		if (!myPartnersInitialized) {
			dispatch(initializeMyPartners())
		}
	}, [dispatch, isInitialized, partnershipsInitialized, invitedUsersInitialized, myPartnersInitialized])

	/**
	 * Accepts or rejects a pending invitation.
	 *
	 * Both buttons were previously `onClick={() => {}}`, so the whole flow
	 * existed end to end — thunk, service, PATCH /api/mypartners/accept — with
	 * nothing calling it.
	 *
	 * The refetch is explicit and awaited rather than left to the init effect.
	 * `acceptMyPartner.pending` resets the slice to its initial state, which
	 * refires that effect immediately, before the server has written anything,
	 * so the list would repopulate with the invitation still in it.
	 */
	const handleInvitationResponse = async (entry, isAccepted) => {
		if (respondingToId) return
		setRespondingToId(entry._id)

		const name = entry.user?.name || 'This partner'

		try {
			await dispatch(acceptMyPartner({ partnershipId: entry._id, isAccepted })).unwrap()

			await Promise.all([
				dispatch(getMyPendingInvitations()),
				dispatch(getMyPendingPartnerShips()),
				dispatch(initializeMyPartners()),
			])

			// The dialog has nothing left to show once the last one is handled.
			if (myPendingInvitationsList.length <= 1) setShowInvitationsDialog(false)

			notify({
				title: isAccepted ? 'Partnership Accepted!' : 'Invitation Declined',
				text: isAccepted ? `You and ${name} are now partners.` : `${name}'s invitation has been declined.`,
				icon: isAccepted ? 'success' : 'info',
			})
		} catch (err) {
			notify({
				title: 'Something went wrong',
				text: err?.message || `We could not ${isAccepted ? 'accept' : 'decline'} this invitation. Please try again.`,
				icon: 'error',
			})
		} finally {
			setRespondingToId(null)
		}
	}

	/**
	 * Withdraws an invitation the user sent, after confirming. Destructive and
	 * not undoable, so unlike accept/reject it asks first.
	 */
	const handleWithdrawConnection = async entry => {
		if (respondingToId) return

		const name = entry.partner?.name || 'this partner'

		const confirmed = await notify({
			title: 'Withdraw invitation?',
			text: `${name} will no longer see your invitation. You can invite them again later.`,
			icon: 'warning',
			buttons: {
				cancel: { text: 'Cancel', value: null, visible: true, closeModal: true },
				confirm: { text: 'Withdraw', value: true, visible: true, closeModal: true },
			},
			dangerMode: true,
		})

		if (!confirmed) return

		setRespondingToId(entry._id)

		try {
			await dispatch(withdrawMyPartnerInvitation(entry._id)).unwrap()
			await dispatch(getMyPendingPartnerShips())

			if (myPendingPartnerShipsList.length <= 1) setShowConnectionsDialog(false)

			notify({
				title: 'Invitation Withdrawn',
				text: `Your invitation to ${name} has been cancelled.`,
				icon: 'success',
			})
		} catch (err) {
			notify({
				title: 'Something went wrong',
				text: err?.message || 'We could not withdraw this invitation. Please try again.',
				icon: 'error',
			})
		} finally {
			setRespondingToId(null)
		}
	}

	/**
	 * Sends the invitation email again. The invitation record already exists and
	 * is untouched, so this is purely a nudge; the "Sent" date deliberately
	 * still shows when the invitation was first created.
	 */
	const handleResendConnection = async entry => {
		if (respondingToId) return
		setRespondingToId(entry._id)

		const name = entry.partner?.name || 'this partner'

		try {
			// The server sends it now (MYD-547); the browser's EmailJS copy only
			// ever reached info@.
			await myPartnersService().resendMyPartnerInvitation(entry._id)

			notify({
				title: 'Invitation Resent',
				text: `We have sent your invitation to ${name} again.`,
				icon: 'success',
			})
		} catch (err) {
			notify({
				title: 'Something went wrong',
				text: 'We could not resend the invitation email. Please try again.',
				icon: 'error',
			})
		} finally {
			setRespondingToId(null)
		}
	}

	const handleSearch = e => {
		const { value } = e.target
		setSearchInputValue(value)
		setFilterFn({
			fn: items => {
				if (value === '') return items
				const lowerValue = value.toLowerCase()
				return items.filter(
					x =>
						(x.name && x.name.toLowerCase().includes(lowerValue)) ||
						(x.firstName && x.firstName.toLowerCase().includes(lowerValue)) ||
						(x.company && x.company.toLowerCase().includes(lowerValue)) ||
						(x.email && x.email.toLowerCase().includes(lowerValue)) ||
						(x.title && x.title.toLowerCase().includes(lowerValue))
				)
			},
		})

		setTimeout(() => {
			searchInputRef.current.focus()
		}, 0)
	}

	const handleConnect = async partner => {
		notify({
			title: `Partnership Invitation!`,
			text: `Add ${partner.name} as a partner to your teams?`,
			icon: 'success',
			buttons: {
				cancel: {
					text: 'Cancel',
					value: null,
					visible: true,
					className: '',
					closeModal: true,
				},
				confirm: {
					text: 'Connect',
					value: true,
					visible: true,
					className: '',
					closeModal: true,
				},
			},
		}).then(willDelete => {
			if (willDelete) {
				dispatch(addMyPartner({ partner: partner._id }))
				notify({
					title: `Invitation Sent!`,
					text: `Congratulations! ${partner.name} has been informed of your partnership invitation. Once they accept you two will both be partners.`,
					icon: 'success',
				})
				// The server emails them when the invitation is created (MYD-547).
			} else {
				notify({
					title: `Invitation Canceled.`,
					text: `No invitation will be sent.`,
					icon: 'info',
				})
			}
		})
		dispatch(initializeUsers())
		dispatch(initializeMyPartners())
		dispatch(getMyPendingPartnerShips())
	}

	const handleInviteUser = async (newUser, resetForm) => {
		const emailUser = await dispatch(inviteNewUser({ newUser, inviterId: userid }))

		const { payload } = emailUser

		// The server emails the invitation when it creates it (MYD-547).
		if (payload._id) {
			notify({
				title: `Partner Invited!`,
				text: `Good job. You've invited ${newUser.name} to partner with you. An invitation to join MYDealTeams has been emailed to ${newUser.firstName || newUser.name}.`,
				icon: 'success',
				button: 'Ok',
			})
		}

		resetForm()
		setOpenPopup(false)
		dispatch(initializeUsers())
		dispatch(initializeMyPartners())
		dispatch(getMyPendingPartnerShips())
	}

	const addPartnersHandler = () => {
		setOpenPopup(true)
	}

	// Filter out pending and already-partnered users
	let nonPendingList = []

	if (users?.length > 0 && myPendingInvitationsList?.length > 0 && nonPendingList?.length === 0) {
		nonPendingList = users.filter(
			user => !myPendingInvitationsList.find(partner => partner.user._id === user._id) && user
		)
	} else {
		nonPendingList = users
	}

	let newPartnerList = []

	if (nonPendingList?.length > 0 && myPartnersList?.length > 0 && newPartnerList?.length === 0) {
		newPartnerList = nonPendingList.filter(
			nonPendingList => !myPartnersList.find(partner => partner.partner._id === nonPendingList._id) && nonPendingList
		)
	} else {
		newPartnerList = nonPendingList
	}

	const { recordsAfterPagingAndSorting } = useTableNoPage(newPartnerList, headerCells, filterFn)

	const partnersToShow = recordsAfterPagingAndSorting()

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						{/* The close button self-gates on viewport, so this row is the same
						    shape on desktop with nothing rendered on the right. */}
						<div className="flex items-center justify-between gap-3 mb-4">
							<h2 className="text-2xl font-semibold text-gray-800">Partners</h2>
							<MobileBackButton />
						</div>

						<Layout.PageToolbarFilter
							buttonText="Invite New Partner"
							buttonOnClick={() => addPartnersHandler()}
							searchInputLabel="Search:"
							searchInputRef={searchInputRef}
							searchInputValue={searchInputValue}
							searchInputOnChange={handleSearch}
						/>

						<div className="max-w-[860px] mx-auto w-full mt-4 bg-white rounded-lg border border-gray-200 p-5">
							{/* Pending status bars */}
							<div className="relative group rounded-lg border border-gray-200 px-4 py-3 mb-3 flex items-center justify-between">
								<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5  bg-blue-50 text-blue-600 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
									Connections that you have invited to join you as partners.
								</div>
								<span className="text-sm font-medium text-gray-700">
									Pending Connections
									<span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600">
										{myPendingPartnerShipsList.length}
									</span>
								</span>
								<ShadButton
									variant="primary"
									isOutline
									className="text-xs"
									onClick={() => setShowConnectionsDialog(true)}
								>
									Manage
								</ShadButton>
							</div>

							<div className="relative group rounded-lg border border-gray-200 px-4 py-3 mb-3 flex items-center justify-between">
								<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-amber-50 text-amber-600 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
									Connections that have invited you to join their network.
								</div>
								<span className="text-sm font-medium text-gray-700">
									Pending Invitations
									<span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
										{myPendingInvitationsList.length}
									</span>
								</span>
								<ShadButton
									variant="primary"
									isOutline
									className="text-xs"
									onClick={() => setShowInvitationsDialog(true)}
								>
									Manage
								</ShadButton>
							</div>

							{userListLoading || myPartnersLoading ? (
								<Spinner containerHeight="50vh" />
							) : error ? (
								<Message variant="danger">{error}</Message>
							) : partnersToShow.length === 0 ? (
								<PartnersEmptyState onInvitePartner={() => setOpenPopup(true)} />
							) : (
								<CardGridCN>
									{partnersToShow.map(partner => (
										<div key={partner._id} className="cursor-pointer hover:scale-105 duration-100">
											<PartnerCard
												name={partner.name}
												title={partner.title}
												companyName={partner.company}
												avatarUrl={partner.image}
												actions={
													<Button
														isOutline
														className="px-3 py-1 text-sm rounded-full"
														onClick={() => handleConnect(partner)}
													>
														Connect
													</Button>
												}
											/>
										</div>
									))}
								</CardGridCN>
							)}
						</div>
					</main>

					<RightPanel storageKey="partners" label="Partnerships panel">
						{/* Partners list -- top half */}
						<div className="flex flex-col h-1/2 border-b border-gray-200">
							<div className="px-4 py-3 border-b border-gray-100">
								<Link
									to="/partners"
									className="flex items-center gap-2 text-gray-600 text-base font-semibold no-underline hover:scale-105 transition-transform duration-150"
								>
									<Users className="w-4 h-4" /> My Partnerships
								</Link>
							</div>
							<div className="flex-1 overflow-y-auto">
								{myPartnersList.length > 0 ? (
									<div className="flex flex-col">
										{myPartnersList.map((partner, index) => (
											<button
												key={index}
												type="button"
												onClick={() => openPartner(partner.partner?._id, partner.partner)}
												className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors"
											>
												<Avatar className="h-8 w-8 flex-shrink-0">
													<AvatarImage src={partner.partner?.image} alt={partner.partner?.name || ''} />
													<AvatarFallback className="text-xs bg-gray-100 text-gray-600">
														{(partner.partner?.name || '?').charAt(0).toUpperCase()}
													</AvatarFallback>
												</Avatar>
												<div className="min-w-0 flex-1">
													<p className="text-sm font-medium text-gray-900 truncate">{partner.partner?.name}</p>
													<p className="text-xs text-gray-500 truncate">
														{partner.isAccepted ? partner.partner?.company : 'Pending'}
													</p>
												</div>
											</button>
										))}
									</div>
								) : (
									<div className="flex items-center justify-center h-full p-4">
										<h3 className="text-[#0080fe] opacity-30 text-sm font-light text-center">No partners yet</h3>
									</div>
								)}
							</div>
						</div>

						{/* Vendor Partnerships -- bottom half */}
						<div className="flex flex-col h-1/2">
							<div className="px-4 py-3 border-b border-gray-100">
								<span className="flex items-center gap-2 text-gray-600 text-base font-semibold">
									<Handshake className="w-4 h-4" /> Vendor Partnerships
								</span>
							</div>
							<div className="flex-1 overflow-y-auto">
								<VendorList />
							</div>
						</div>
					</RightPanel>
				</div>
			</div>

			{partnerDialog}

			<Popup
				title="Invite New Partner"
				openPopup={openPopup}
				setOpenPopup={setOpenPopup}
				icon={<UserPlus className="w-5 h-5" />}
				description="Invite a new partner to collaborate"
			>
				<UserAddNew fromUser={fromUser} userAdd={handleInviteUser} />
			</Popup>

			{/* Pending Connections Dialog */}
			<Dialog open={showConnectionsDialog} onClose={() => setShowConnectionsDialog(false)}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<Users className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Pending Connections</DialogTitle>
								<DialogDescription>View your partner connections</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="divide-y divide-gray-100">
						{myPendingPartnerShipsList.length === 0 ? (
							<div className="px-5 py-8 text-center text-sm text-gray-400">No pending connections</div>
						) : (
							myPendingPartnerShipsList.map(entry => (
								<div key={entry._id} className="flex items-center gap-4 px-5 py-4">
									<Avatar className="h-10 w-10 flex-shrink-0">
										<AvatarImage src={entry.partner?.image} alt={entry.partner?.name || ''} />
										<AvatarFallback className="text-sm bg-gray-100 text-gray-600">
											{(entry.partner?.name || '?').charAt(0).toUpperCase()}
										</AvatarFallback>
									</Avatar>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900 truncate">{entry.partner?.name}</p>
										<p className="text-sm text-gray-500 truncate">
											{entry.partner?.company}
											{entry.createdAt && (
												<span className="text-xs text-gray-400">
													{' · '}Sent {formatInviteDate(entry.createdAt)}
												</span>
											)}
										</p>
									</div>
									<ShadButton
										variant="ghost-red"
										isOutline
										className="flex-shrink-0 text-xs"
										disabled={respondingToId === entry._id}
										onClick={() => handleWithdrawConnection(entry)}
									>
										Withdraw
									</ShadButton>
									<ShadButton
										variant="primary"
										className="flex-shrink-0 text-xs"
										disabled={respondingToId === entry._id}
										onClick={() => handleResendConnection(entry)}
									>
										{respondingToId === entry._id ? 'Working…' : 'Resend'}
									</ShadButton>
								</div>
							))
						)}
					</div>
				</DialogContent>
			</Dialog>

			{/* Pending Invitations Dialog */}
			<Dialog open={showInvitationsDialog} onClose={() => setShowInvitationsDialog(false)}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<UserPlus className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Pending Invitations</DialogTitle>
								<DialogDescription>View pending partner invitations</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="divide-y divide-gray-100">
						{myPendingInvitationsList.length === 0 ? (
							<div className="px-5 py-8 text-center text-sm text-gray-400">No pending invitations</div>
						) : (
							myPendingInvitationsList.map(entry => (
								<div key={entry._id} className="flex items-center gap-4 px-5 py-4">
									<Avatar className="h-10 w-10 flex-shrink-0">
										<AvatarImage src={entry.user?.image} alt={entry.user?.name || ''} />
										<AvatarFallback className="text-sm bg-gray-100 text-gray-600">
											{(entry.user?.name || '?').charAt(0).toUpperCase()}
										</AvatarFallback>
									</Avatar>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900 truncate">{entry.user?.name}</p>
										<p className="text-sm text-gray-500 truncate">
											{entry.user?.company}
											{entry.createdAt && (
												<span className="text-xs text-gray-400">
													{' · '}Received {formatInviteDate(entry.createdAt)}
												</span>
											)}
										</p>
									</div>
									<div className="flex items-center gap-2 flex-shrink-0">
										<ShadButton
											variant="ghost-red"
											isOutline
											className="text-xs"
											disabled={respondingToId === entry._id}
											onClick={() => handleInvitationResponse(entry, false)}
										>
											Reject
										</ShadButton>
										<ShadButton
											variant="primary"
											className="text-xs"
											disabled={respondingToId === entry._id}
											onClick={() => handleInvitationResponse(entry, true)}
										>
											{respondingToId === entry._id ? 'Working…' : 'Accept'}
										</ShadButton>
									</div>
								</div>
							))
						)}
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default Partners_Index
